import { makeStyles, tokens, Tooltip, mergeClasses } from '@fluentui/react-components';
import { isToday, isPast, isSameDay } from 'date-fns';
import type { Reservation } from '@cp-parkingslots/shared';
import { useAuth } from '../../hooks/useAuth';

const useStyles = makeStyles({
  cell: {
    padding: tokens.spacingVerticalXXS,
    minHeight: '32px',
    backgroundColor: tokens.colorNeutralBackground1,
    cursor: 'pointer',
    transition: 'background-color 0.15s ease',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
    // Better touch target
    ':active': {
      backgroundColor: tokens.colorNeutralBackground1Pressed,
    },
  },
  cellCompact: {
    minHeight: '44px', // Minimum touch target size
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  cellToday: {
    backgroundColor: tokens.colorBrandBackground2,
    ':hover': {
      backgroundColor: tokens.colorBrandBackground2Hover,
    },
    ':active': {
      backgroundColor: tokens.colorBrandBackground2Pressed,
    },
  },
  cellPast: {
    backgroundColor: tokens.colorNeutralBackground3,
    cursor: 'default',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground3,
    },
    ':active': {
      backgroundColor: tokens.colorNeutralBackground3,
    },
  },
  cellReserved: {
    cursor: 'default',
  },
  cellReservedOther: {
    backgroundColor: tokens.colorPaletteRedBackground2,
    ':hover': {
      backgroundColor: tokens.colorPaletteRedBackground2,
    },
    ':active': {
      backgroundColor: tokens.colorPaletteRedBackground2,
    },
  },
  cellReservedMine: {
    backgroundColor: tokens.colorPaletteGreenBackground2,
    ':hover': {
      backgroundColor: tokens.colorPaletteGreenBackground2,
    },
    ':active': {
      backgroundColor: tokens.colorPaletteGreenBackground2,
    },
  },
  reservationInfo: {
    fontSize: tokens.fontSizeBase100,
    padding: tokens.spacingHorizontalXXS,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  reservationInfoCompact: {
    fontSize: tokens.fontSizeBase200,
    textAlign: 'center',
    width: '100%',
  },
});

interface TimeSlotCellProps {
  date: Date;
  hour: number;
  slotId: string;
  reservation?: Reservation;
  onClick: () => void;
  compact?: boolean;
}

export default function TimeSlotCell({
  date,
  hour,
  slotId: _slotId,
  reservation,
  onClick,
  compact = false,
}: TimeSlotCellProps) {
  const styles = useStyles();
  const { user } = useAuth();

  const slotDate = new Date(date);
  slotDate.setHours(hour, 0, 0, 0);

  const now = new Date();
  const isPastSlot = isPast(slotDate) && !isSameDay(slotDate, now);
  const isTodaySlot = isToday(date);
  const isMyReservation = reservation && user && reservation.userId === user.id;

  const getCellClass = () => {
    const classes = [styles.cell];

    if (compact) {
      classes.push(styles.cellCompact);
    }

    if (isPastSlot) {
      classes.push(styles.cellPast);
    } else if (isTodaySlot) {
      classes.push(styles.cellToday);
    }

    if (reservation) {
      classes.push(styles.cellReserved);
      if (isMyReservation) {
        classes.push(styles.cellReservedMine);
      } else {
        classes.push(styles.cellReservedOther);
      }
    }

    return mergeClasses(...classes);
  };

  const handleClick = () => {
    if (!isPastSlot && !reservation) {
      onClick();
    }
  };

  // Get display name - show initials on compact mode, full name otherwise
  const getDisplayName = () => {
    if (!reservation) return null;
    if (compact) {
      // Show initials or first name for compact view
      const parts = reservation.userName.split(' ');
      if (parts.length >= 2) {
        return parts.map(p => p[0]).join('').toUpperCase();
      }
      return reservation.userName.substring(0, 3);
    }
    return reservation.userName;
  };

  const cellContent = reservation ? (
    <div className={mergeClasses(
      styles.reservationInfo,
      compact && styles.reservationInfoCompact
    )}>
      {getDisplayName()}
    </div>
  ) : null;

  if (reservation) {
    return (
      <Tooltip
        content={`${reservation.userName} (${reservation.startHour}:00 - ${reservation.endHour}:00)`}
        relationship="label"
      >
        <div className={getCellClass()} onClick={handleClick}>
          {cellContent}
        </div>
      </Tooltip>
    );
  }

  return (
    <div className={getCellClass()} onClick={handleClick}>
      {cellContent}
    </div>
  );
}
